import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Image as ImageIcon, Lock, Play, X } from 'lucide-react';
import Button from '@/components/ui/Button';
import { api } from '@/lib/api';
import { bidi, BIDI_CLS } from '@/lib/bidi';
import { cn } from '@/lib/cn';

interface PickFile {
  id: string; filename: string; kind: string; uploaded_at?: string | null; content_type?: string | null;
  thumbnail_url?: string | null; default: boolean; client_approved: boolean; with_clinic_now: boolean;
}

/** Approving the final review: pick the files that go to the clinic (Aziz 2026-10-26: only
 *  the final of this update, nothing else). Older versions stay with the team. Files the
 *  clinic already approved stay with it and are shown locked. */
export default function FinalPickDialog({ creativeId, busy, onCancel, onConfirm }: {
  creativeId: string; busy: boolean; onCancel: () => void; onConfirm: (ids: string[] | undefined) => void;
}) {
  const q = useQuery({
    queryKey: ['final-pick', creativeId],
    queryFn: async () => (await api.get<{ files: PickFile[]; last_change_request_at?: string | null }>(`/creatives/${creativeId}/final-pick`)).data,
  });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  useEffect(() => { if (q.data) setPicked(new Set(q.data.files.filter((f) => f.default).map((f) => f.id))); }, [q.data]);
  const open = useMemo(() => (q.data?.files ?? []).filter((f) => !f.client_approved), [q.data]);
  const locked = (q.data?.files ?? []).filter((f) => f.client_approved);
  // group by upload day so the newest version of each video is easy to see
  const groups = useMemo(() => {
    const m = new Map<string, PickFile[]>();
    for (const f of open) {
      const k = f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Unknown date';
      m.set(k, [...(m.get(k) ?? []), f]);
    }
    return Array.from(m.entries());
  }, [open]);
  const toggle = (id: string) => setPicked((x) => { const n = new Set(x); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Pick the final files" data-testid="final-pick">
      <div className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl border border-line bg-bg-card shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-line">
          <div>
            <h2 className="text-base font-semibold text-muted-strong">Which files are the final?</h2>
            <p className="text-xs text-muted mt-0.5">Only the files you tick go to the clinic. Older versions stay with the team.
              {q.data?.last_change_request_at ? ` Ticked for you: the newest upload since the clinic's last change request (${new Date(q.data.last_change_request_at).toLocaleDateString('en-AE')}).` : ' Ticked for you: the newest upload.'}</p>
          </div>
          <button onClick={onCancel} aria-label="Close" className="text-muted hover:text-muted-strong"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4">
          {q.isLoading && <p className="text-sm text-muted">Loading the files...</p>}
          {q.data && open.length === 0 && <p className="text-sm text-muted">No final, static or poster file to pick: the clinic gets the cut this piece delivers (the final edit was marked not needed).</p>}
          {groups.map(([day, files]) => (
            <div key={day}>
              <div className="flex items-center justify-between text-[11px] text-muted mb-1.5">
                <span className="uppercase tracking-wide">Uploaded {day} · {files.length} file{files.length === 1 ? '' : 's'}</span>
                <button type="button" className="text-accent-400 hover:underline" onClick={() => setPicked((x) => {
                  const n = new Set(x); const all = files.every((f) => n.has(f.id));
                  files.forEach((f) => (all ? n.delete(f.id) : n.add(f.id))); return n;
                })}>{files.every((f) => picked.has(f.id)) ? 'Untick these' : 'Tick these'}</button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {files.map((f) => {
                  const on = picked.has(f.id); const vid = (f.content_type ?? '').startsWith('video');
                  return (
                    <label key={f.id} className={cn('relative rounded-lg border p-1.5 cursor-pointer', on ? 'border-emerald-500 bg-emerald-500/10' : 'border-line hover:border-slate-500')} data-testid="final-pick-file">
                      <input type="checkbox" checked={on} onChange={() => toggle(f.id)} className="absolute top-2.5 left-2.5 h-4 w-4 accent-emerald-500" />
                      <div className="w-full aspect-video rounded bg-black overflow-hidden mb-1 flex items-center justify-center text-white/70">
                        {f.thumbnail_url ? <img src={f.thumbnail_url} alt="" className="w-full h-full object-cover" /> : vid ? <Play size={16} /> : <ImageIcon size={16} />}
                      </div>
                      <div className="text-[10px] text-muted-strong truncate" title={f.filename}><span dir="auto" className={BIDI_CLS}>{bidi(f.filename)}</span></div>
                      {f.with_clinic_now && <div className="text-[9px] text-sky-300">with the clinic now</div>}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
          {locked.length > 0 && (
            <p className="text-[11px] text-emerald-300 inline-flex items-center gap-1"><Lock size={11} /> {locked.length} file{locked.length === 1 ? '' : 's'} the clinic already approved stay with the clinic.</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-line">
          <span className="mr-auto text-xs text-muted">{picked.size} of {open.length} ticked</span>
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button size="sm" icon={<CheckCircle2 size={12} />} loading={busy} disabled={!q.data || (open.length > 0 && picked.size === 0)}
            onClick={() => onConfirm(open.length ? Array.from(picked) : undefined)} data-testid="final-pick-confirm">
            {open.length ? `Approve and send ${picked.size} to the clinic` : 'Approve'}
          </Button>
        </div>
      </div>
    </div>
  );
}
